import { useState, useEffect, useCallback, useMemo } from 'react';
import { EDGE_THRESHOLDS, DEFAULTS, TOOLTIP_OFFSET } from './constants';

/**
 * Custom hook to calculate optimal tooltip position based on viewport boundaries
 * @param {boolean} isVisible - Whether tooltip is currently visible
 * @param {React.RefObject} triggerRef - Ref to the trigger element
 * @param {React.RefObject} tooltipRef - Ref to the tooltip element
 * @param {string} placement - Desired placement ('auto' or specific position)
 * @param {number} maxWidth - Maximum width for tooltip
 * @param {object} offset - Additional offset adjustments {x, y}
 * @returns {object} - Style object and calculated placement
 */
export const useTooltipPosition = (
  isVisible,
  triggerRef,
  tooltipRef,
  placement = 'auto',
  maxWidth = DEFAULTS.MAX_WIDTH,
  offset = { x: 0, y: 0 }
) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [calculatedPlacement, setCalculatedPlacement] = useState(placement);

  const calculateOptimalPlacement = useCallback((triggerRect) => {
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    // Calculate percentages
    const horizontalPosition = triggerRect.left / viewport.width;
    const verticalPosition = triggerRect.top / viewport.height;

    // Determine if near edges
    const isRightEdge = horizontalPosition > EDGE_THRESHOLDS.RIGHT;
    const isLeftEdge = horizontalPosition < EDGE_THRESHOLDS.LEFT;
    const isTopEdge = verticalPosition < EDGE_THRESHOLDS.TOP;
    const isBottomEdge = verticalPosition > EDGE_THRESHOLDS.BOTTOM;

    // Auto placement logic
    if (placement === 'auto') {
      if (isRightEdge && !isTopEdge && !isBottomEdge) return 'bottom-right';
      if (isLeftEdge && !isTopEdge && !isBottomEdge) return 'bottom-left';
      if (isTopEdge) return 'bottom-center';
      if (isBottomEdge) return 'top-center';
      return 'bottom-left'; // default
    }

    return placement;
  }, [placement]);

  const calculatePosition = useCallback(() => {
    if (!isVisible || !triggerRef.current || !tooltipRef.current) {
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
      scrollY: window.scrollY,
      scrollX: window.scrollX
    };

    // Determine optimal placement
    const optimalPlacement = calculateOptimalPlacement(triggerRect);
    setCalculatedPlacement(optimalPlacement);

    // Get offset configuration for this placement
    const placementOffset = TOOLTIP_OFFSET[optimalPlacement] || { x: 0.25, y: 0.5 };

    let top = 0;
    let left = 0;

    // Calculate position based on placement
    switch (optimalPlacement) {
      case 'bottom-left':
        top = triggerRect.top + viewport.scrollY + (triggerRect.height * placementOffset.y);
        left = triggerRect.left + viewport.scrollX + (triggerRect.width * placementOffset.x);
        break;

      case 'bottom-right':
        top = triggerRect.top + viewport.scrollY + (triggerRect.height * placementOffset.y);
        left = triggerRect.right + viewport.scrollX - (triggerRect.width * Math.abs(placementOffset.x)) - tooltipRect.width;
        break;

      case 'bottom-center':
        top = triggerRect.bottom + viewport.scrollY + DEFAULTS.EDGE_MARGIN;
        left = triggerRect.left + viewport.scrollX + (triggerRect.width / 2) - (tooltipRect.width / 2);
        break;

      case 'top-center':
        top = triggerRect.top + viewport.scrollY - tooltipRect.height - DEFAULTS.EDGE_MARGIN;
        left = triggerRect.left + viewport.scrollX + (triggerRect.width / 2) - (tooltipRect.width / 2);
        break;

      case 'top-left':
        top = triggerRect.top + viewport.scrollY - tooltipRect.height - DEFAULTS.EDGE_MARGIN;
        left = triggerRect.left + viewport.scrollX + (triggerRect.width * placementOffset.x);
        break;

      case 'top-right':
        top = triggerRect.top + viewport.scrollY - tooltipRect.height - DEFAULTS.EDGE_MARGIN;
        left = triggerRect.right + viewport.scrollX - (triggerRect.width * Math.abs(placementOffset.x)) - tooltipRect.width;
        break;

      case 'left':
        top = triggerRect.top + viewport.scrollY + (triggerRect.height / 2) - (tooltipRect.height / 2);
        left = triggerRect.left + viewport.scrollX - tooltipRect.width - DEFAULTS.EDGE_MARGIN;
        break;

      case 'right':
        top = triggerRect.top + viewport.scrollY + (triggerRect.height / 2) - (tooltipRect.height / 2);
        left = triggerRect.right + viewport.scrollX + DEFAULTS.EDGE_MARGIN;
        break;

      default:
        top = triggerRect.top + viewport.scrollY + (triggerRect.height * 0.5);
        left = triggerRect.left + viewport.scrollX + (triggerRect.width * 0.25);
    }

    // Apply additional offset
    top += offset.y;
    left += offset.x;

    // Ensure tooltip stays within viewport bounds
    const maxLeft = viewport.width - tooltipRect.width - DEFAULTS.EDGE_MARGIN;
    const maxTop = viewport.height + viewport.scrollY - tooltipRect.height - DEFAULTS.EDGE_MARGIN;

    left = Math.max(DEFAULTS.EDGE_MARGIN, Math.min(left, maxLeft));
    top = Math.max(viewport.scrollY + DEFAULTS.EDGE_MARGIN, Math.min(top, maxTop));

    setPosition({ top, left });
  }, [isVisible, triggerRef, tooltipRef, calculateOptimalPlacement, offset]);

  // Calculate position when visible or on resize
  useEffect(() => {
    if (isVisible) {
      calculatePosition();

      const handleResize = () => {
        calculatePosition();
      };

      const handleScroll = () => {
        calculatePosition();
      };

      // Debounce resize handler
      let resizeTimeout;
      const debouncedResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(handleResize, 150);
      };

      window.addEventListener('resize', debouncedResize);
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        window.removeEventListener('resize', debouncedResize);
        window.removeEventListener('scroll', handleScroll, true);
        clearTimeout(resizeTimeout);
      };
    }
  }, [isVisible, calculatePosition]);

  const style = useMemo(() => ({
    position: 'absolute',
    top: `${position.top}px`,
    left: `${position.left}px`,
    maxWidth: `${maxWidth}px`,
  }), [position, maxWidth]);

  return {
    style,
    placement: calculatedPlacement
  };
};
